/**
 * 실행 상태 스토어(Zustand) — **3분할 중 "실행" 축**.
 *
 * 상태를 셋으로 나눈다(F §5 F-2): 실행 상태는 여기(Zustand), 서버 데이터는 TanStack Query,
 * 화면 로컬은 컴포넌트 useState. 스트리밍처럼 초당 수십 번 바뀌는 상태를 Query 에 넣으면
 * 캐시 무효화가 의미를 잃고, 반대로 서버 데이터를 여기 넣으면 재요청·동기화를 손으로 짜게 된다.
 *
 * 스토어는 **이벤트를 리듀서에 넘길 뿐** 판정하지 않는다 — 판정이 여기 섞이면 순수 함수로
 * 테스트한 불변식이 스토어에서 다시 깨질 수 있다.
 */
import { create } from 'zustand'

import { UnauthorizedError } from '@/api/client'
import { resumeGate, sendMessage, uploadFile } from './api'
import type { GateDecision, UploadedFile } from './api'
import { applyRunEvent, appendUserMessage, clearGate } from './reducer'
import { initialRunState } from './events'
import type { RunEvent, RunState } from './events'
import { loadTranscript, saveTranscript } from './transcript'

/** 마지막 대화 키 — 새로고침 후 어디로 돌아갈지. 이력 **본문**은 서버가 갖는다(U2). */
const THREAD_STORAGE_KEY = 'elaine.threadId'

interface ChatStore extends RunState {
  /** 첨부 대기 목록 — 전송 시 file_refs 로 실리고 비워진다. */
  attachments: UploadedFile[]
  /**
   * 이 대화가 속한/속할 프로젝트(F-6).
   *
   * 새 대화면 첫 전송에 실려 소속이 정해지고, 지난 대화를 열면 그 대화의 소속으로 채워진다.
   * **표시가 아니라 전송값**이다 — 그래서 사이드바가 아니라 실행 상태 축에 산다.
   */
  projectId: string | null
  /** 프로젝트 안에서 새 대화를 시작한다(전송 전이므로 아직 서버에 아무것도 없다). */
  startInProject: (projectId: string | null) => void
  send: (message: string) => Promise<void>
  respondToGate: (decision: GateDecision) => Promise<void>
  attach: (file: File) => Promise<void>
  removeAttachment: (fileRef: string) => void
  /** 스트림 중단 — 서버 저작은 계속된다(B: job 은 요청 수명 밖). 화면만 끊는다. */
  stop: () => void
  reset: () => void
  /** 새로고침 복원 — 마지막 thread 의 이력을 서버에서 되불러온다(U2). */
  restore: () => Promise<void>
  /**
   * 사이드바에서 지난 대화를 연다 — 진행 중 스트림은 끊고 그 thread 의 이력으로 갈아탄다.
   * projectId 는 목록이 이미 아는 값을 그대로 넘겨받는다(서버에 다시 묻지 않는다).
   */
  openThread: (threadId: string, projectId?: string | null) => Promise<void>
}

/**
 * 스트림 수명 계측 (F-0 §1.3 M1 이관분).
 *
 * 2026-07-20 사고 때 "언제 끊겼는지"를 못 봐서 nginx 타임스탬프로 역산해야 했다. 구 Nitro 는
 * 이걸 stdout 에 남겼고(파일로 tee), 브라우저로 옮기면 수집이 안 된다 — 그래서 **최소한
 * 개발자도구에서 재현 가능하도록** 콘솔에 같은 모양으로 남긴다. 서버 수집이 필요해지면
 * 여기 한 곳에서 보고 엔드포인트를 붙인다(버리지 말 것).
 */
function traceStream(phase: 'start' | 'end' | 'fail', startedAt: number, detail = ''): void {
  const elapsed = Math.round(performance.now() - startedAt)
  const line = `[chat] stream ${phase} after ${elapsed}ms${detail ? `: ${detail}` : ''}`
  if (phase === 'fail') console.error(line)
  else console.info(line)
}

export const useChatStore = create<ChatStore>((set, get) => {
  let controller: AbortController | null = null

  /** 이벤트 스트림을 소비해 상태에 접는다. 중단·오류도 상태로 표현한다(막다른 종료 금지). */
  async function consume(events: AsyncGenerator<RunEvent>): Promise<void> {
    const startedAt = performance.now()
    traceStream('start', startedAt)
    try {
      for await (const event of events) {
        set((state) => applyRunEvent(state, event))
      }
      traceStream('end', startedAt)
      void persist()
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        // 사용자가 멈춘 것 — 오류가 아니다. 서버 저작은 계속 돈다(B).
        traceStream('end', startedAt, 'aborted by user')
        set({ status: 'idle' })
        return
      }
      const message =
        error instanceof UnauthorizedError
          ? error.message
          : error instanceof Error
            ? error.message
            : '스트림이 끊겼습니다.'
      traceStream('fail', startedAt, message)
      set({ status: 'error', error: message })
    } finally {
      controller = null
    }
  }

  /** 턴이 끝날 때마다 이력을 서버에 남긴다 — 실패해도 대화를 막지 않는다. */
  async function persist(): Promise<void> {
    const { threadId, messages } = get()
    if (!threadId || messages.length === 0) return
    localStorage.setItem(THREAD_STORAGE_KEY, threadId)
    await saveTranscript(threadId, messages)
  }

  return {
    ...initialRunState,
    attachments: [],
    projectId: null,

    startInProject(projectId: string | null) {
      controller?.abort()
      localStorage.removeItem(THREAD_STORAGE_KEY)
      set({ ...initialRunState, attachments: [], projectId })
    },

    async restore() {
      const threadId = localStorage.getItem(THREAD_STORAGE_KEY)
      if (!threadId) return
      const messages = await loadTranscript(threadId)
      // 이력이 비어도 threadId 는 살린다 — 그래야 진행 중인 저작(job)을 다시 붙어 볼 수 있다.
      set({ threadId, messages, status: 'idle' })
    },

    async openThread(threadId: string, projectId: string | null = null) {
      if (get().threadId === threadId) return
      // 보던 대화의 스트림을 끊는다 — 서버 저작은 계속 돈다(B). 화면만 옮겨 탄다.
      controller?.abort()
      const messages = await loadTranscript(threadId)
      // 이력이 비어도 threadId 는 살린다(restore 와 같은 이유 — 진행 중 저작에 다시 붙는다).
      localStorage.setItem(THREAD_STORAGE_KEY, threadId)
      // 소속은 목록이 이미 알고 있다(threads 응답의 project_id) — 여기서 다시 묻지 않고
      // 호출부가 넘긴다. 모르면 null 로 두는 편이 낫다: 틀린 소속으로 다음 턴을 보내면
      // 백엔드가 무시하긴 하지만, 화면이 잘못된 프로젝트를 가리키게 된다.
      set({ ...initialRunState, threadId, messages, attachments: [], projectId })
    },

    async send(message: string) {
      const text = message.trim()
      if (!text || get().status === 'streaming') return
      const fileRefs = get().attachments.map((a) => a.file_ref)
      set((state) => ({ ...appendUserMessage(state, text), attachments: [] }))
      controller = new AbortController()
      await consume(
        sendMessage({
          message: text,
          threadId: get().threadId,
          // 첫 턴에만 백엔드가 본다(이미 소속이 있는 thread 면 무시). 매번 실어 보내도
          // 안전하므로 '첫 턴인지'를 프런트가 따로 세지 않는다 — 그 계산이 틀리는 편이
          // 위험하다(소속이 조용히 안 붙는다).
          projectId: get().projectId,
          fileRefs,
          signal: controller.signal,
        }),
      )
    },

    async respondToGate(decision: GateDecision) {
      const { gate, threadId } = get()
      const target = gate?.threadId ?? threadId
      if (!gate || !target) return
      set((state) => clearGate(state)) // 버튼부터 걷는다 — 중복 클릭이 발주를 두 번 만들지 않게
      controller = new AbortController()
      await consume(resumeGate(target, decision, controller.signal))
    },

    async attach(file: File) {
      const uploaded = await uploadFile(file)
      set((state) => ({ attachments: [...state.attachments, uploaded] }))
    },

    removeAttachment(fileRef: string) {
      set((state) => ({ attachments: state.attachments.filter((a) => a.file_ref !== fileRef) }))
    },

    stop() {
      controller?.abort()
    },

    reset() {
      controller?.abort()
      localStorage.removeItem(THREAD_STORAGE_KEY)
      // '새 대화'는 프로젝트 밖으로 나오는 것이기도 하다 — 방금 프로젝트에서 대화하다
      // 새 대화를 누르면 그 프로젝트에 계속 묶이는 편이 더 놀랍다.
      set({ ...initialRunState, attachments: [], projectId: null })
    },
  }
})
